import { message } from 'antd';
import Router from 'next/router';
import { memo, useCallback, useContext, useMemo, useState } from 'react';

import type { TemplateDetailVO } from '@/3.0/ai-viral-template/_components/template-ui/types';
import { ViralTemplateTab } from '@/3.0/ai-viral-template/hooks/useViralTemplateRouteState';
import {
  buildInitialViralTemplateFormData,
  normalizeViralTemplateSubmitFormData,
  validateViralTemplateFormData,
} from '@/3.0/ai-viral-template/utils/templateForm';
import Button from '@/components/common/Button';
import InlineVideoWithControls from '@/components/common/VideoPlayer/InlineVideoWithControls';
import { FunctionNameEnum } from '@/components/RightProvider/const';
import { RightContext } from '@/components/RightProvider/context';
import type { UseTranslateType } from '@/hooks/useTranslate';
import { submitViralTemplateTask } from '@/services/viralTemplate';
import { AIFunction } from '@/utils/const';
import { EventNewID, SubscribeReasonEnum } from '@/utils/eventlog/eventNewConfig';
import { pushGTMEvent } from '@/utils/eventlog/gtmDataLayer';
import { trackEvent } from '@/utils/eventlog/logAdapter';
import { viralTemplateURL } from '@/utils/routeConstants';
import { APP_VERSION } from '@/version';

import CommonModal, { AICreditModal, PaymentModal } from '..';
import { isViralTemplateSubmitDisabled } from './submitState';
import { FIELD_BORDER, TemplateDetailFieldRow } from './TemplateFormFields';

export interface ViralTemplateModalOptions {
  detail: TemplateDetailVO;
  /** 仅使用列表接口返回的 `modelFee.amount` */
  creditCost: number;
  templateCategory: string;
}

const TemplateDetailModalBody = memo(function TemplateDetailModalBodyInner({
  detail,
  creditCost,
  templateCategory,
  t,
  onSubmitSuccess,
}: {
  detail: TemplateDetailVO;
  creditCost: number;
  templateCategory: string;
  t: UseTranslateType;
  onSubmitSuccess: () => void;
}) {
  const rightContext = useContext(RightContext);
  const [formData, setFormData] = useState(() =>
    buildInitialViralTemplateFormData(detail),
  );
  const [submitting, setSubmitting] = useState(false);

  const onFieldChange = useCallback((key: string, next: unknown) => {
    setFormData((prev) => ({ ...prev, [key]: next }));
  }, []);

  const isFormValid = useMemo(
    () => validateViralTemplateFormData(detail, formData).ok,
    [detail, formData],
  );
  const isSubmitDisabled = useMemo(
    () =>
      isViralTemplateSubmitDisabled({
        isFormValid,
        submitting,
        creditCost,
      }),
    [creditCost, isFormValid, submitting],
  );

  const handleSubmit = useCallback(async () => {
    if (!validateViralTemplateFormData(detail, formData).ok) return;
    pushGTMEvent('app_use_start', {
      app_name: 'Template',
      app_version: APP_VERSION,
      feature: 'Template',
      template_name: detail.name,
      template_category: templateCategory,
      credits_required: creditCost,
    });
    trackEvent(EventNewID.TASK_SUBMIT, {
      source: AIFunction.ViralTemplate,
      model_id: null,
      template_name: detail.name,
    });
    setSubmitting(true);
    try {
      const normalizedFormData = normalizeViralTemplateSubmitFormData(detail, formData);
      const res = await submitViralTemplateTask({
        id: detail.templateId,
        formData: normalizedFormData,
      });
      const ok = res?.success === true || res?.code === 200;
      if (ok) {
        message.success(t('viralTemplate.form.submitSuccess'));
        trackEvent(EventNewID.CONSUME_CREDITS, {
          source: AIFunction.ViralTemplate,
          credits: creditCost,
        });
        /** 「我的创作」列表与素材库一致走 `fetchTaskList` → POST `/api/rest/dlt/f/cloud/my/create`；展示见爆款页该 Tab */
        await Router.push({
          pathname: viralTemplateURL,
          query: { tab: ViralTemplateTab.MY_CREATIONS },
        });
        onSubmitSuccess();
        return;
      }
      message.error(res?.message || t('viralTemplate.form.submitFailed'));
    } catch {
      message.error(t('viralTemplate.form.submitFailed'));
    } finally {
      setSubmitting(false);
    }
  }, [creditCost, detail, formData, onSubmitSuccess, t, templateCategory]);

  const handleClick = useCallback(() => {
    // 权限校验
    const hasPermission = rightContext.getEntryValue(FunctionNameEnum.F_VIRAL_TEMPLATE);
    if (!hasPermission) {
      PaymentModal.show({
        feature: AIFunction.ViralTemplate,
        customTip: t('bc.removal.nopermission'),
      });
      return;
    }

    // 积分校验
    if (creditCost > rightContext.currentVip.balance) {
      if (rightContext.currentVip.key === 'freeVip') {
        PaymentModal.show({
          feature: AIFunction.ViralTemplate,
          reason: SubscribeReasonEnum.topUp,
          customTip: t('No-Credit-Please-Purchase'),
        });
      } else {
        AICreditModal.show({
          feature: AIFunction.ViralTemplate,
          reason: SubscribeReasonEnum.topUp,
        });
      }
      return;
    }

    handleSubmit();
  }, [creditCost, handleSubmit, rightContext, t]);

  return (
    <div
      className="flex min-h-[320px] min-w-0 flex-row gap-[16px] border-t border-solid"
      style={{ borderTopColor: FIELD_BORDER }}
    >
      <div className="mt-[12px] ml-[24px] mb-[24px] mr-[0] h-[523px] w-[294px] shrink-0 overflow-hidden rounded-[12px] bg-black">
        <InlineVideoWithControls src={detail.preview} poster={detail.cover} loop />
      </div>
      <div
        className="mt-[12px] flex min-h-0 min-w-0 flex-1 flex-col border-l border-solid"
        style={{ borderLeftColor: FIELD_BORDER }}
      >
        <div className="min-h-0 max-h-[480px] flex-1 overflow-y-auto no-scrollbar pb-[12px] pl-[16px] pr-[24px]">
          <div className="flex flex-col gap-[16px] pr-[4px]">
            {detail.formSchema.fields.map((field) => (
              <div key={field.key} className="flex flex-col gap-[8px]">
                <TemplateDetailFieldRow
                  field={field}
                  value={formData[field.key]}
                  onFieldChange={onFieldChange}
                  t={t}
                />
              </div>
            ))}
          </div>
        </div>
        <div
          className="mt-auto flex min-h-[60px] shrink-0 flex-col items-center justify-between gap-[4px] border-t border-solid py-[5px]"
          style={{ borderTopColor: FIELD_BORDER }}
        >
          <span className="truncate text-[12px] text-[#4931FA]">
            <span className="truncate text-[12px] text-[#999]">
              {t('fa0edb42055c73b7934ff8c79adf9138')}
            </span>
            {creditCost != null
              ? `${creditCost} ${t('merror.sub.cre')}`
              : `— ${t('merror.sub.cre')}`}
          </span>
          <div className="flex shrink-0 flex-row items-center gap-[10px]">
            <Button
              style={{ fontSize: '18px' }}
              loading={submitting}
              disabled={isSubmitDisabled}
              onClick={handleClick}
              className="!h-[40px] !w-[280px] !rounded-[6px] !bg-[#4931FA] !font-normal  whitespace-nowrap"
            >
              {t('home.tpl.generate')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
});

TemplateDetailModalBody.displayName = 'TemplateDetailModalBody';

export function viralTemplateModalShow(
  options: ViralTemplateModalOptions,
): Promise<boolean> {
  const { detail, creditCost, templateCategory } = options;

  return CommonModal.show((t) => ({
    width: 880,
    title: t('viralTemplate.form.title'),
    footer: false,
    customPadding: {
      x: 0,
      y: 0,
    },
    content: (closeModal, _contentRef, tr, resolve) => (
      <TemplateDetailModalBody
        key={detail.templateId}
        detail={detail}
        creditCost={creditCost}
        templateCategory={templateCategory}
        t={tr}
        onSubmitSuccess={() => {
          closeModal();
          resolve(true);
        }}
      />
    ),
  }));
}
